/**
 * @fileoverview `useMfaTranslate(t)` — the MFA corpus's translator.
 * @package @puffin-core/mfa-ui
 *
 * ## The bug this closes (AAS-I18N-001 close-out, 2026-08-18)
 *
 * `MfaVerification` resolved its copy through `useMfaTranslator(externalT)`, whose documented
 * fallback is the shipped ENGLISH `DEFAULT_MFA_STRINGS`. The `t` prop was optional, and a census of
 * the four render sites found it passed at **one**:
 *
 *   | mount | passed `t`? |
 *   | ----- | ----------- |
 *   | `login-shell/components/LoginForm.jsx` | ✅ |
 *   | `re-auth/components/PasswordForm.jsx` | ❌ |
 *   | `pos-web/features/auth/context/MfaChallengeProvider.jsx` | ❌ |
 *   | `port-web/features/auth/pages/MfaSetupPage.jsx` | ❌ |
 *
 * So the MFA challenge rendered English in every locale on three of four paths — on the
 * security-critical step, under an `<html lang="es">` that Q-QF4-3 had made correct, which turns the
 * mismatch into a WCAG SC 3.1.1 failure in the other direction.
 *
 * `web/no-hardcoded-strings` scores this **0** in every app and always will: the literals live in a
 * package and the gate censuses `apps/**`. RECIPE miss-class 9 — the same shape as the `auth-shell`
 * defect that shipped "Skip to main content" in English across five apps, and closed the same way.
 *
 * ## Resolution order (unchanged)
 *
 *   1. the consumer's `t` prop — still honoured FIRST, so the published component contract does not
 *      change for `LoginForm`, the one caller that already supplies one;
 *   2. the `mfa` i18next namespace on the instance in the React tree — registered by
 *      `loadMfaStrings`, wired for every app through `@puffin-core/i18n-namespaces`;
 *   3. `DEFAULT_MFA_STRINGS` — the English degrade path. An app that never registers the namespace
 *      renders exactly what it rendered before: no crash, no raw dotted key.
 *
 * ## I18N-RENDER-002 Phase 0 (2026-08-20) — bound from the shared factory
 *
 * Five `use<Pkg>Translate` hooks existed and had drifted into **three** shapes (1-param vs 2-param
 * `chained`, 2-arg vs 3-arg `resolveString`, guarded vs unguarded vs absent interpolation). They are
 * behaviourally equivalent once the interpolator's `onMissing` policy carries the difference, so one
 * factory now serves all of them and the two recorded traps live in ONE place:
 *
 *   - **`params` is forwarded only when present** — `consumerT(key, undefined)` is not the same call
 *     as `consumerT(key)`, and this package's own suite asserts the exact arity with
 *     `toHaveBeenLastCalledWith(key)`;
 *   - **interpolation happens in the hook** — react-i18next's no-instance `t` returns a value
 *     WITHOUT interpolating it, which is how a literal `{{label}}` shipped into a live `aria-label`.
 *
 * ⚠️ Hook-placement rule (RECIPE §3): call this **immediately above the first computed hook that
 * consumes the translator**. Higher and `web/component-state-order` reports every translator-free
 * `useCallback` beneath it; lower and you ship a TDZ crash that lint scores 0 on.
 */

import { createInterpolator } from '@puffin-core/i18n-bootstrap/strings-kit';
import { createNamespaceTranslate } from '@puffin-core/i18n-bootstrap/namespace-translate';

import { MFA_NAMESPACE } from '../strings/namespace.js';
import { resolveString } from '../strings/resolveString.js';

/**
 * Build the MFA corpus translator for a component.
 *
 * ⚠️ `braces: 'double'` + `onMissing: 'empty'` is this corpus's contract and differs from
 * `@puffin-core/re-auth`'s deliberately — see `strings/en.js`.
 *
 * @param {((key: string, params?: object) => string|undefined|null)} [consumerT] - The optional `t`
 *   prop a caller may pass. Kept first in the resolution order so the existing component contract is
 *   unchanged.
 * @returns {(key: string, params?: object) => string|undefined} A translator resolving
 *   consumer → `mfa` namespace → English default, then expanding any `{{param}}` placeholders.
 */
export const useMfaTranslate = createNamespaceTranslate({
  namespace: MFA_NAMESPACE,
  resolveString,
  interpolate: createInterpolator({ braces: 'double', onMissing: 'empty' }),
});

export default useMfaTranslate;
